import { useChatStore } from '@stores/chatStore';
import { type ApiErrorResponse, safeParseJson } from '@utils/api';
import {
  createContext,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { API_BASE_URL } from '../config/api';
import {
  reloadToRoot,
  SIGN_OUT_MARKER_KEY,
  SIGNED_IN_FLAG_KEY,
  type SignOutReason,
  signOutClient,
  takeSignOutReason,
} from '../signOutCleanup';
import {
  authAwareFetch,
  fetchWithCredentials,
  refreshAccessToken,
  setSessionEndedHandler,
} from './authHttpUtils';
import type { AuthContextValue, AuthProviderProps, User } from './authTypes';
import { normalizeUser } from './authTypes';
import { useAuthOperations } from './useAuthOperations';

export type {
  AuthContextValue,
  AuthProviderProps,
  AuthResponse,
  RegisterOptions,
  User,
  UserPreferences,
} from './authTypes';

const API_BASE = API_BASE_URL;

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// =============================================================================
// AuthProvider Component
// =============================================================================

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoutReason, setLogoutReason] = useState<SignOutReason | null>(takeSignOutReason);

  // Becoming signed out clears storage and reloads (signOutCleanup.ts), carrying the logout reason.
  const signOut = useCallback((reason: SignOutReason | null) => {
    useChatStore.getState().resetLocal();
    signOutClient(reason);
  }, []);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        // Try to get current user (cookies will be sent automatically)
        let res = await fetchWithCredentials(`${API_BASE}/api/auth/me`);

        if (!res.ok && res.status === 401) {
          // Try to refresh token
          const refreshResult = await refreshAccessToken();
          if (refreshResult.success) {
            res = await fetchWithCredentials(`${API_BASE}/api/auth/me`);
          } else {
            const reason: SignOutReason | null =
              refreshResult.reason === 'Account is paused'
                ? 'paused'
                : refreshResult.reason === 'Account is deleted'
                  ? 'deleted'
                  : null;
            if (reason) setLogoutReason(reason);
            // The session ended while the app was closed, so storage may still hold that user's data.
            if (!refreshResult.isUnreachable && localStorage.getItem(SIGNED_IN_FLAG_KEY)) {
              signOut(reason);
              return;
            }
          }
        }

        if (res.ok) {
          const data = await safeParseJson<{ user?: User; error?: string }>(res);
          if (!(data as ApiErrorResponse).error && (data as { user?: User }).user) {
            const userData = (data as { user: User }).user;
            setUser(normalizeUser(userData));
          }
        }
      } catch (_err) {}

      setLoading(false);
    };

    checkAuth();
  }, [signOut]);

  // Refetch user data (for email verification status updates)
  const refetchUser = useCallback(async (): Promise<void> => {
    try {
      const res = await authAwareFetch(`${API_BASE}/api/auth/me`);
      if (res.ok) {
        const data = await safeParseJson<{ user?: User; error?: string }>(res);
        if (!(data as ApiErrorResponse).error && (data as { user?: User }).user) {
          const userData = (data as { user: User }).user;
          setUser(normalizeUser(userData));
        }
      }
    } catch (_err) {}
  }, []);

  const operations = useAuthOperations({
    authFetch: authAwareFetch,
    setUser,
    setError,
    refetchUser,
  });

  // A refused refresh ends the session exactly as signing out does, rather than leave a
  // signed-in UI whose every request fails. Only once: later refusals find it already ended.
  const { logout } = operations;
  const isSignedIn = useRef(false);
  useEffect(() => {
    isSignedIn.current = user !== null;
  }, [user]);

  // Sign Out, an ended session, delete and pause all end in setUser(null).
  const wasSignedIn = useRef(false);
  const latestLogoutReason = useRef(logoutReason);
  latestLogoutReason.current = logoutReason;
  useEffect(() => {
    if (user) {
      wasSignedIn.current = true;
      localStorage.setItem(SIGNED_IN_FLAG_KEY, '1');
      return;
    }
    if (!wasSignedIn.current) return;
    wasSignedIn.current = false;
    signOut(latestLogoutReason.current);
  }, [user, signOut]);

  // Another tab signed out, so this tab's session has ended too.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === SIGN_OUT_MARKER_KEY && event.newValue !== null) reloadToRoot();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  useEffect(() => {
    setSessionEndedHandler((reason) => {
      if (!isSignedIn.current) return;
      isSignedIn.current = false;
      if (reason === 'Account is paused') setLogoutReason('paused');
      else if (reason === 'Account is deleted') setLogoutReason('deleted');
      void logout();
    });
    return () => setSessionEndedHandler(null);
  }, [logout]);

  // Compute needsOnboarding: true if user exists but hasn't completed onboarding
  const needsOnboarding = Boolean(user && !user.onboardingCompletedAt);
  const clearLogoutReason = useCallback(() => setLogoutReason(null), []);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    logoutReason,
    clearLogoutReason,
    refetchUser,
    needsOnboarding,
    authFetch: authAwareFetch,
    ...operations,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// useAuth Hook
// =============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
